/** Public Iconify HTTP API (browser + server fetch). */
const ICONIFY_API = "https://api.iconify.design";

export type ParsedIconifyId = { prefix: string; name: string };

export function parseIconifyIconId(iconId: string): ParsedIconifyId | null {
	const t = iconId.trim();
	const i = t.indexOf(":");
	if (i <= 0 || i === t.length - 1) {
		return null;
	}
	const prefix = t.slice(0, i).trim();
	const name = t.slice(i + 1).trim();
	if (!(prefix && name)) {
		return null;
	}
	return { prefix, name };
}

type IconifyIconJson = {
	body?: string;
	width?: number;
	height?: number;
	left?: number;
	top?: number;
};

type IconifyCollectionResponse = {
	width?: number;
	height?: number;
	icons?: Record<string, IconifyIconJson>;
};

/**
 * Strip risky constructs from an SVG string before injecting into the DOM.
 * Iconify bodies are trusted path data, but we still reject scripts and listeners.
 */
export function sanitizeIconifySvgMarkup(raw: string): string | null {
	const trimmed = raw.trim();
	if (!/^<svg\b/i.test(trimmed)) {
		return null;
	}
	let s = trimmed.replace(/<script\b[\s\S]*?<\/script>/gi, "");
	s = s.replace(/<foreignObject\b[\s\S]*?<\/foreignObject>/gi, "");
	s = s.replace(/\s(on[a-z]+)\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "");
	s = s.replace(/\bhref\s*=\s*["']?\s*javascript:/gi, 'href="#"');
	s = s.replace(/\bxlink:href\s*=\s*["']?\s*javascript:/gi, 'xlink:href="#"');
	return s;
}

export function svgMarkupFromIconifyCollectionJson(
	data: IconifyCollectionResponse,
	iconName: string
): string | null {
	const icon = data.icons?.[iconName];
	if (!icon || typeof icon.body !== "string" || !icon.body.trim()) {
		return null;
	}
	const vbW = icon.width ?? data.width ?? 24;
	const vbH = icon.height ?? data.height ?? 24;
	const vbX = icon.left ?? 0;
	const vbY = icon.top ?? 0;
	const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${vbX} ${vbY} ${vbW} ${vbH}" preserveAspectRatio="xMidYMid meet" width="100%" height="100%">${icon.body}</svg>`;
	return sanitizeIconifySvgMarkup(svg);
}

/** Builds sanitized inline SVG from Iconify JSON (`{collection}.json?icons={name}`). */
export async function fetchIconifySvgMarkup(
	iconId: string
): Promise<string | null> {
	const p = parseIconifyIconId(iconId);
	if (!p) {
		return null;
	}
	const url = `${ICONIFY_API}/${encodeURIComponent(p.prefix)}.json?icons=${encodeURIComponent(p.name)}`;
	try {
		const res = await fetch(url);
		if (!res.ok) {
			return null;
		}
		const data = (await res.json()) as IconifyCollectionResponse;
		return svgMarkupFromIconifyCollectionJson(data, p.name);
	} catch {
		return null;
	}
}

/** Iconify search (`icons[]` are full ids like `mdi:home`). */
export async function searchIconifyIcons(
	query: string,
	limit = 24
): Promise<string[]> {
	const q = query.trim();
	if (q.length < 2) {
		return [];
	}
	try {
		const url = `${ICONIFY_API}/search?query=${encodeURIComponent(q)}&limit=${String(limit)}`;
		const res = await fetch(url);
		if (!res.ok) {
			return [];
		}
		const data = (await res.json()) as { icons?: string[] };
		return Array.isArray(data.icons) ? data.icons.slice(0, limit) : [];
	} catch {
		return [];
	}
}

/** Default seed for new icon blocks — Material Design Icons “home”. */
export const DEFAULT_ICONIFY_ICON_ID = "mdi:home";

/** Inline SVG for {@link DEFAULT_ICONIFY_ICON_ID} (same shape as Iconify JSON API). */
export const DEFAULT_ICONIFY_ICON_SVG_MARKUP =
	'<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet" width="100%" height="100%"><path fill="currentColor" d="M10 20v-6h4v6h5v-8h3L12 3L2 12h3v8z"/></svg>';
